export type UnknownRecord = Record<string, unknown>;

type QueryWithAnd = {
  $and: unknown[];
};

const hasOwn = <K extends PropertyKey>(
  value: object,
  key: K
): value is object & Record<K, unknown> => Object.prototype.hasOwnProperty.call(value, key);

const isQueryWithAnd = (value: unknown): value is QueryWithAnd => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  if (!hasOwn(value, "$and")) {
    return false;
  }

  return Array.isArray((value as { $and: unknown }).$and);
};

/**
 * 쿼리 객체가 $and 배열을 포함하는지 타입 가드
 * @param q - 검사할 쿼리 객체
 * @returns $and 배열을 포함하면 true
 * @example
 * if (hasAnd(params.query)) {
 *   const conditions = params.query.$and; // type-safe access
 * }
 */
export const hasAnd = (q: unknown): q is { $and: unknown[] } =>
  typeof q === "object" && q !== null && Array.isArray((q as Record<string, unknown>)["$and"]);

/**
 * $and 절을 포함하는 쿼리 객체를 안전하게 직렬화
 * - $and가 없거나 빈 배열이면 undefined 반환
 *
 * @param value - 직렬화할 쿼리 객체
 * @returns JSON 문자열 또는 undefined
 *
 * @example
 * serializeAndQuery({ $and: [{ name: "test" }] }) // '{"$and":[{"name":"test"}]}'
 * serializeAndQuery({ $and: [] }) // undefined
 * serializeAndQuery({ other: "field" }) // undefined
 */
export const serializeAndQuery = (value: unknown): string | undefined => {
  if (!isQueryWithAnd(value)) {
    return undefined;
  }

  if (value.$and.length === 0) {
    return undefined;
  }

  return JSON.stringify(value);
};

/**
 * 쿼리 객체를 정규화하여 Record 형태로 반환
 * @param value - 정규화할 쿼리 값
 * @returns 정규화된 객체 또는 undefined
 */
export const normalizeQueryObject = (value: unknown): Record<string, unknown> | undefined => {
  if (isQueryWithAnd(value)) {
    return { $and: value.$and };
  }

  if (isRecord(value)) {
    return value;
  }

  return undefined;
};

/**
 * 값이 Record 타입인지 타입 가드
 * @param value - 검사할 값
 * @returns Record 타입이면 true
 */
export const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const isNonEmptyArray = (value: unknown): value is unknown[] =>
  Array.isArray(value) && value.length > 0;

/**
 * 쿼리 값에 비어있지 않은 $and 조건이 있는지 확인
 * @param value - 검사할 쿼리 값
 * @returns 비어있지 않은 $and 조건이 있으면 true
 */
export const hasNonEmptyAnd = (value: unknown): boolean => {
  if (isNonEmptyArray(value)) {
    return true;
  }

  if (!isRecord(value)) {
    return false;
  }

  const andConditions = value["$and"];
  return isNonEmptyArray(andConditions);
};

/**
 * 쿼리 조건을 안전하게 타입 체크하여 JSON 문자열로 변환
 * - 배열이면 { $and: 배열 } 형태로 감싸서 변환
 * - 객체면 그대로 변환
 * - 그 외는 빈 객체 "{}" 또는 undefined 반환
 *
 * @param value - 쿼리 조건 (배열, 객체, 또는 null/undefined)
 * @param options.requireConditions - true일 경우 빈 조건이면 undefined 반환
 * @returns JSON 문자열 또는 undefined
 *
 * @example
 * // 배열을 전달하면 $and로 감싸짐
 * stringifyQuery([{ name: "test" }]) // '{"$and":[{"name":"test"}]}'
 *
 * @example
 * // 객체를 전달하면 그대로 변환
 * stringifyQuery({ $and: [{ name: "test" }] }) // '{"$and":[{"name":"test"}]}'
 *
 * @example
 * // requireConditions: true면 빈 조건은 undefined 반환
 * stringifyQuery([], { requireConditions: true }) // undefined
 */
export const stringifyQuery = (
  value: unknown,
  { requireConditions = false }: { requireConditions?: boolean } = {}
): string | undefined => {
  if (Array.isArray(value)) {
    if (value.length === 0 && requireConditions) {
      return undefined;
    }

    return JSON.stringify({ $and: value });
  }

  if (!isRecord(value)) {
    return requireConditions ? undefined : "{}";
  }

  const andConditions = value["$and"];

  if (!Array.isArray(andConditions)) {
    return requireConditions ? undefined : JSON.stringify(value);
  }

  if (andConditions.length === 0 && requireConditions) {
    return undefined;
  }

  return JSON.stringify(value);
};

/**
 * 객체에서 문자열 속성을 안전하게 추출
 * @param value - 검사할 객체
 * @param key - 추출할 속성 키
 * @returns 문자열 값 또는 undefined
 *
 * @example
 * getStringProperty({ name: "test" }, "name") // "test"
 * getStringProperty({ age: 25 }, "age") // undefined
 */
export const getStringProperty = (value: unknown, key: string): string | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const candidate = value[key];
  return typeof candidate === "string" ? candidate : undefined;
};

/**
 * 객체에서 불리언 속성을 안전하게 추출
 * @param value - 검사할 객체
 * @param key - 추출할 속성 키
 * @returns 불리언 값 또는 undefined
 *
 * @example
 * getBooleanProperty({ active: true }, "active") // true
 * getBooleanProperty({ name: "test" }, "name") // undefined
 */
export const getBooleanProperty = (value: unknown, key: string): boolean | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const candidate = value[key];
  return typeof candidate === "boolean" ? candidate : undefined;
};
